/**
 * Canonical brand palette — change colors here and in `src/styles/brand-variables.css`
 * (plain CSS cannot import JS; keep the two files aligned when tokens change).
 *
 * Use in React: `import { brandPalette } from "../constants/brandPalette"`
 * Use in Chakra: `color="brand.primary"` etc. (see chakra.config.js)
 * Use in .css: `color: var(--brand-primary)`
 */

export const brandInk = "#2E2A26";

export const brandPalette = {
  primary: "#5B0D16",
  /** Darker hover / pressed (legacy accent) */
  accentDark: "#450B11",
  secondary: "#702933",
  wine: "#852F3D",
  crimson: "#9C4654",
  cream: "#F7F2EC",
  paper: "#FBF9F6",
  sand: "#F4EFE9",
  warmBeige: "#EDE6DD",
  /** Subtle border / divider. Flips to a translucent light edge in dark mode
   *  via the `--brand-stone` CSS variable in `src/styles/brand-variables.css`. */
  stone: "var(--brand-stone)",
  taupe: "#958D85",
};

/**
 * Dark-mode palette for the admin area. Maroon accents are intentionally
 * reused from `brandPalette` so the sidebar and active states stay consistent
 * across light/dark modes.
 */
export const darkPalette = {
  canvas: "#1A1614",
  surface: "#221C1A",
  surfaceRaised: "#2A2320",
  surfaceMuted: "#1E1A18",
  border: "rgba(247, 242, 236, 0.08)",
  borderStrong: "rgba(247, 242, 236, 0.18)",
  textPrimary: "#F1E9DF",
  textMuted: "rgba(241, 233, 223, 0.65)",
};

/** RGB tuples (no `rgb()`) for `rgba(var(--brand-*-rgb), a)` in CSS or template strings */
export const brandRgb = {
  primary: "91, 13, 22",
  secondary: "112, 41, 51",
  stoneWash: "213, 205, 197",
};

/** Chakra `styles.global.body` — consumes CSS variables from
 *  `src/styles/brand-variables.css` so it flips automatically when the
 *  AdminThemeProvider sets `data-theme="dark"` on <html>. */
export const brandBodyGlobalStyle = {
  bg: "var(--brand-body-bg)",
  backgroundImage: "var(--brand-body-gradient)",
  backgroundAttachment: "fixed",
  color: "var(--text-primary)",
  transition: "background-color 200ms ease, color 200ms ease",
};

/** Chakra `colors.brand` scale-style object (flat names for clarity) */
export const brandChakraColors = {
  maroon: brandPalette.primary,
  burgundy: brandPalette.secondary,
  wine: brandPalette.wine,
  crimson: brandPalette.crimson,
  cream: brandPalette.cream,
  white: brandPalette.paper,
  sand: brandPalette.sand,
  beige: brandPalette.warmBeige,
  stone: brandPalette.stone,
  taupe: brandPalette.taupe,
};

/**
 * Theme-aware CSS variable names matching `src/styles/brand-variables.css`.
 * Use these as raw CSS values in Chakra style props, e.g.
 * `<Box bg={brandCssVars.surfaceRaised} color={brandCssVars.textPrimary} />`.
 * Their resolved values flip automatically when `data-theme="dark"` is set
 * on <html> by the AdminThemeProvider.
 */
export const brandCssVars = {
  surfaceCanvas: "var(--surface-canvas)",
  surfaceRaised: "var(--surface-raised)",
  surfaceMuted: "var(--surface-muted)",
  textPrimary: "var(--text-primary)",
  textMuted: "var(--text-muted)",
  borderSubtle: "var(--border-subtle)",
};
